import React, { useState,useEffect } from "react";
import { toast } from 'react-toastify';
import {format, parseISO } from 'date-fns';
import Slider from 'react-slick';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';
import { getDefaultReviewList,getSingleReviewTemplateDetail,searchYelpBusinessReview } from "../../../../utils/reviewApi";
import { getSingleWidgetDetail } from "../../../../staticWidgets/utils/CommonWidgetApi";
import RatingStars from "../../../../components/RatingStars";
import ReviewPopup from "./ReviewPopup";
import BadgeWidget from "./BadgeWidget";
import FloatingWidget from "./FloatingWidget";

const CommonWidget = ({selectedLayout,isTemplateId,widgetId,reviewTabData}) => {
    const [apiData, setApiData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [selectedReview, setSelectedReview] = useState(null);
    const [visibleGridCount, setVisibleGridCount] = useState(8);
    const [visibleListLayoutCount, setVisibleListLayoutCount] = useState(5);
    const [visibleMasonryLayoutCount, setVisibleMasonryLayoutCount] = useState(8);

    // Default values for the reviewTabData
    const {
        is_website_frontend,
        layout_is_full_width,
        business_name,
        layout_custom_width,
        style_background_color,
        header_is_widget_title,
        header_title,
        header_caption,
        setting_rating_format,
        style_widget_title_color,
        style_widget_title_fonts_title_size,
        style_widget_title_caption_color,
        style_widget_title_fonts_caption_size,
        style_header_background_color,
        header_is_show_header,
        header_write_reviews_button,
        header_source_icon,
        header_rating,
        header_number_of_reviews,
        style_header_text_color,
        style_header_write_review_background_color,
        style_header_write_review_text_color,
        header_write_review_button_type,
        style_rating_stars_color,
        layout_rows_on_mobile,
        layout_rows,
        style_load_more_button_color,
        style_load_more_button_text_color
    } = reviewTabData || {};

    // Map layout name to a specific class
    const layoutClassMap = {
        carousel_layout: 'upgadeify-carousel-style',
        list_layout: 'upgadeify-list-style',
        grid_layout: 'upgadeify-grid-style',
        masonry_layout: 'upgadeify-masonry-style',
        slider_layout: 'upgadeify-slider-style',
    };
    const layoutClass = layoutClassMap[selectedLayout] || '';

    //Helper to get slidesToShow from width
    const getSlidesToShow = (width) => {
        const w = Number(width);
        if (w >= 1024) return 3;
        if (w >= 768) return 2;
        return 1;
    };
    //State use for handle responsive logic
    const [slidesToShow, setSlidesToShow] = useState(getSlidesToShow(layout_custom_width));

    //Update slidesToShow when window resizes or width changes
    useEffect(() => {
        const handleResize = () => {
        setSlidesToShow(getSlidesToShow(layout_custom_width));
        };
        handleResize();
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, [layout_custom_width]);

    //Carousel Slider Settings
    const carouselSliderSettings = {
        dots: false,
        infinite: true,
        speed: 500,
        slidesToShow: slidesToShow,
        slidesToScroll: 1,
        arrows: true,
        autoplay: false,
        responsive: [
        {
            breakpoint: 768,
            settings: {
                slidesToShow: 1,
                slidesToScroll: 1,
                arrows: true,
                dots: false,
            },
        },
        ],
    };

    //Slider Settings
    const sliderSettings = {
        dots: true,
        infinite: true,
        speed: 500,
        slidesToShow: slidesToShow,
        slidesToScroll: 3,
        arrows: false,
        autoplay: false,
        responsive: [
            {
                breakpoint: 768,
                settings: {
                    slidesToShow: 1,
                    slidesToScroll: 1,
                    arrows: true,
                    dots: false,
                },
            },
        ],
    };

    // Fetching Review data
    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            try {
                let result;
                if (isTemplateId) {
                    result = await getSingleReviewTemplateDetail(isTemplateId);
                } else if (widgetId) {
                    result = await getSingleWidgetDetail(widgetId);
                } else if (business_name) {
                    const payload = { business_name: business_name };
                    result = await searchYelpBusinessReview(payload);
                } else {
                    let app_type = 'Yelp_Review';
                    result = await getDefaultReviewList(app_type);
                }
                setApiData(result); 
            } catch (error) {
                setApiData(null); 
                toast.warning(error.message);
            } finally {
                setLoading(false); 
            }
        };

        fetchData();
    }, [isTemplateId, widgetId, business_name]);

    //Handle Layout Rows and Columns
    const layoutColumns = 3;
    // Simple: detect if the screen is mobile once at render time
    const isMobile = window.innerWidth <= 768;
    const layoutRows = isMobile
        ? layout_rows_on_mobile
        : layout_rows;

    useEffect(() => {
        const newGridCount = layoutRows * layoutColumns;
        const newMasonryCount = layoutRows * layoutColumns;

        setVisibleGridCount(newGridCount);
        setVisibleListLayoutCount(layoutRows);
        setVisibleMasonryLayoutCount(newMasonryCount);
    }, [layoutRows, layoutColumns]);

    // Open popup when clicking "Read More"
    const handleReadMoreClick = (review) => {
        setSelectedReview(review);
    };

    // Close the popup
    const handleClosePopup = () => {
        setSelectedReview(null);
    };

    // Load More List Layout function
    const handleGridLoadMore = () => {
        setVisibleGridCount((prevCount) => prevCount + 8);
    };
    
    // Load List Layout More function
    const handleListLayoutLoadMore = () => {
        setVisibleListLayoutCount((prevCount) => prevCount + 5);
    };

    // Load Masonry Layout More function
    const handleMasonryLayoutLoadMore = () => {
        setVisibleMasonryLayoutCount((prevCount) => prevCount + 8);
    };

    //Handle dynamic styles based on layout
    const dynamicStyle =
        selectedLayout !== 'badge_layout' && selectedLayout !== 'floating_badge_layout'
            ? Number(layout_is_full_width) === 1
            ? {
                backgroundColor: style_background_color,
                margin: '0 auto',
            }
            : {
                backgroundColor: style_background_color,
                maxWidth: `${layout_custom_width}px`,
                margin: '0 auto',
                }
            : {};

    // Get grid layout style based on custom width
    const getGridLayoutStyle = () => {
        if (selectedLayout !== 'grid_layout') return {};

        if (layout_custom_width > 1280 && layout_custom_width < 1921) {
            return {
                gridTemplateColumns: 'repeat(auto-fill, minmax(24%, 1fr))',
            
            };
        }

        if (layout_custom_width > 991 && layout_custom_width < 1280) {
            return {
                gridTemplateColumns: 'repeat(auto-fill, minmax(32%, 1fr))',
        
            };
        }

        if (layout_custom_width > 540 && layout_custom_width < 991) {
            return {
                gridTemplateColumns: 'repeat(auto-fill, minmax(48%, 1fr))',
            
            };
        }

        if (layout_custom_width > 0 && layout_custom_width < 540) {
            return {
                gridTemplateColumns: 'repeat(auto-fill, minmax(100%, 1fr))',
            
            };
        }

        // Default fallback
        return {
            gridTemplateColumns: 'repeat(auto-fill, minmax(32%, 1fr))',
        };
    };

    return (
        <div className={`upgadeify-review-template-widget upgadeify ${layoutClass} ${selectedReview ? 'carousel-popup-open' : ''} ${is_website_frontend}`} style={dynamicStyle}>
             {/* Loader and No Api Data Handling */}
            {loading ? (
                <div className="loader-container">
                    <div className="spinner"></div>
                    <p>Loading please wait...</p>
                </div>
            ) : apiData?.status === true ? (
            <>
                {/* Show Header Section Accordgin to condition*/}
                {selectedLayout !== 'badge_layout' && selectedLayout !== 'floating_badge_layout' && (
                    <>
                    {Number(header_is_widget_title) === 1 && (
                        <>
                            <div className="upgadeify-yelp-main-title" style={{ color: style_widget_title_color,fontSize: style_widget_title_fonts_title_size }}>{header_title || ""}</div>
                            <div className="upgadeify-yelp-main-caption" style={{ color: style_widget_title_caption_color,fontSize: style_widget_title_fonts_caption_size }}>{header_caption || ""}</div>
                        </>
                    )}
                    {Number(header_is_show_header) === 1 &&
                        (Number(header_source_icon) === 1 ||
                            Number(header_rating) === 1 ||
                            Number(header_number_of_reviews) === 1 ||
                            Number(header_write_reviews_button) === 1) && (
                            <div className="upgadeify-yelp-reviews-header" style={{ backgroundColor: style_header_background_color,}}>
                        
                                <div className='upgadeify-yelp-reviews-header-left'>
                                    {Number(header_source_icon) === 1 && (
                                    <div className='upgadeify-yelp-reviews-header-title'>
                                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 72 36" className="injected-svg"><g clip-path="url(#a-276)"><path fill="#FF1A1A" d="m57.802 18.346 1.167-.27c.038-.008.076-.018.114-.03a1.288 1.288 0 0 0 .92-1.533l-.006-.02a1.286 1.286 0 0 0-.198-.434 1.622 1.622 0 0 0-.476-.408 4.388 4.388 0 0 0-.677-.313l-1.278-.466c-.717-.267-1.435-.527-2.16-.78-.468-.167-.866-.314-1.213-.42-.065-.02-.137-.04-.195-.06-.419-.13-.711-.182-.96-.183a1.143 1.143 0 0 0-.485.088c-.16.069-.303.17-.42.299a2.547 2.547 0 0 0-.166.209 2.46 2.46 0 0 0-.245.488 6.634 6.634 0 0 0-.35 2.178c.006.67.024 1.529.392 2.11.088.15.207.28.35.38.261.18.526.205.802.225.411.029.81-.072 1.208-.164l3.873-.896h.003Zm13.006-6.193a6.623 6.623 0 0 0-1.266-1.805 2.514 2.514 0 0 0-.435-.332 2.48 2.48 0 0 0-.24-.114 1.155 1.155 0 0 0-.983.05c-.222.11-.463.287-.783.584a2.938 2.938 0 0 1-.15.14c-.263.25-.557.556-.907.912-.54.546-1.07 1.095-1.599 1.65l-.945.98a4.42 4.42 0 0 0-.47.579 1.62 1.62 0 0 0-.25.576 1.27 1.27 0 0 0 .01.477l.006.02a1.285 1.285 0 0 0 1.497.973c.051-.007.092-.016.117-.021l5.04-1.166c.398-.092.801-.177 1.157-.384.24-.14.468-.277.623-.554a1.24 1.24 0 0 0 .149-.495c.077-.687-.282-1.467-.57-2.07Zm-9.022 2.122c.366-.46.364-1.143.396-1.703.11-1.868.225-3.737.316-5.607.036-.707.112-1.406.069-2.12-.034-.588-.039-1.264-.41-1.747-.658-.852-2.056-.781-3.01-.648a8.97 8.97 0 0 0-.876.164c-.29.07-.578.146-.86.237-.915.3-2.203.85-2.42 1.906-.123.597.169 1.206.395 1.752.273.66.648 1.253.99 1.874.9 1.641 1.82 3.27 2.736 4.902.273.486.571 1.103 1.1 1.355a.69.69 0 0 0 .107.04c.237.09.495.109.743.05l.045-.01c.228-.063.434-.186.596-.359a.949.949 0 0 0 .083-.086Zm-.435 5.003a1.165 1.165 0 0 0-1.46-.37c-.08.04-.157.089-.23.143-.21.155-.386.358-.547.56-.041.052-.078.12-.127.166l-.81 1.116c-.46.624-.913 1.25-1.362 1.887-.293.411-.546.758-.747 1.065-.038.058-.078.123-.113.174-.24.372-.377.643-.446.884-.053.16-.07.33-.049.499.023.174.081.342.173.492.049.076.101.148.157.219.121.14.258.265.407.374.56.39 1.173.67 1.819.886a6.428 6.428 0 0 0 1.657.316 2.545 2.545 0 0 0 .552-.043 2.52 2.52 0 0 0 .258-.076 1.172 1.172 0 0 0 .709-.701c.093-.234.154-.531.195-.97.004-.064.013-.14.019-.208.031-.365.047-.794.07-1.3.039-.775.07-1.547.095-2.323l.05-1.377c.012-.319.003-.67-.086-.985a1.468 1.468 0 0 0-.184-.428Zm9.153 2.154c-.17-.186-.409-.371-.788-.6-.054-.03-.118-.07-.177-.106-.315-.189-.695-.389-1.138-.63a134.79 134.79 0 0 0-2.05-1.098l-1.215-.644c-.063-.018-.127-.063-.186-.092a3.016 3.016 0 0 0-.738-.264 1.557 1.557 0 0 0-.268-.027 1.01 1.01 0 0 0-.174.01 1.167 1.167 0 0 0-.974.963 1.51 1.51 0 0 0 .017.464c.06.324.205.643.354.923l.649 1.216c.36.686.727 1.368 1.102 2.047.242.443.442.823.63 1.137.037.06.077.123.108.177.23.378.414.617.6.787.121.117.266.206.426.261.166.055.343.075.518.058.089-.011.177-.027.265-.047.086-.023.17-.05.254-.082a1.88 1.88 0 0 0 .261-.122 6.44 6.44 0 0 0 1.352-1.012 7.123 7.123 0 0 0 1.247-1.593 2.512 2.512 0 0 0 .202-.517 2.44 2.44 0 0 0 .044-.265 1.255 1.255 0 0 0-.06-.519 1.153 1.153 0 0 0-.261-.425Z"></path><path fill="#000" fill-rule="evenodd" d="M28.67 9.605c0-1.002.816-1.82 1.801-1.82.972 0 1.76.818 1.802 1.834V23.83a1.82 1.82 0 0 1-1.802 1.82 1.8 1.8 0 0 1-1.802-1.82V9.605Zm-1.675 8.778c-.014.282-.084.804-.45 1.186-.395.41-.93.507-1.24.507-1.287.008-2.575.011-3.863.015-1.287.003-2.576.007-3.864.014.114.353.339.89.846 1.383.31.297.604.466.731.536.155.099.69.381 1.351.381.704 0 1.323-.225 1.929-.536l.05-.027c.42-.217.855-.442 1.314-.524.451-.07.93.015 1.282.324.422.368.591.905.436 1.455-.183.62-.703 1.157-1.252 1.482a6.36 6.36 0 0 1-.986.465 7.43 7.43 0 0 1-2.716.423c-.225 0-.464 0-.704-.027-.251-.032-.5-.08-.746-.142a5.3 5.3 0 0 1-1.126-.38 6.172 6.172 0 0 1-1.028-.58 6.711 6.711 0 0 1-.9-.776 6.971 6.971 0 0 1-.747-.93c-.76-1.158-1.111-2.584-1.041-3.967.057-1.354.507-2.709 1.323-3.81.087-.13.183-.252.288-.367.041-.047.082-.094.12-.141 1.009-1.233 2.356-1.623 2.911-1.784l.03-.009a6.311 6.311 0 0 1 3.45 0c.267.071 1.773.537 2.94 1.793.058.057.212.24.41.508.5.68.866 1.448 1.081 2.265l.002.007c.085.31.17.733.17 1.256Zm-8.262-2.329a3.179 3.179 0 0 0-1.098 1.779h6.136a3.276 3.276 0 0 0-1.111-1.779 3.208 3.208 0 0 0-1.971-.705c-.267 0-1.14.042-1.956.705Zm23.335-3.74c-1.603 0-3.068.72-4.109 1.892v-.043a1.795 1.795 0 1 0-3.59.1v13.562a1.8 1.8 0 0 0 1.803 1.806c.999 0 1.801-.804 1.801-1.807v-4.233c1.042 1.157 2.492 1.89 4.11 1.89 3.223 0 5.84-2.92 5.84-6.561-.014-3.642-2.632-6.605-5.855-6.605Zm-.788 10.218c-1.829 0-3.321-1.594-3.321-3.584 0-2.005 1.478-3.6 3.321-3.6 1.83 0 3.322 1.595 3.322 3.6-.014 1.989-1.492 3.584-3.322 3.584Zm-29.71-2.667-.592 1.299a65.427 65.427 0 0 0-.788 1.721c-.082.177-.162.358-.245.543-1.125 2.502-2.46 5.473-5.37 6.105-1.408.31-3.534-.114-4.321-1.44-.789-1.341.38-2.88 1.885-2.527.238.054.472.17.707.287.392.196.79.395 1.222.306.407-.07.661-.367.984-.747a3.51 3.51 0 0 0 .732-1.398.672.672 0 0 1-.02-.05.663.663 0 0 0-.021-.048c-.368-.684-.687-1.295-.954-1.806-.175-.333-.326-.622-.454-.862l-.615-1.165c-.45-.853-.524-.993-.722-1.361a51.754 51.754 0 0 0-1.38-2.442c-.521-.875-1.055-1.849-.62-2.879a1.721 1.721 0 0 1 2.112-.988c.906.275 1.307 1.137 1.683 1.944.068.145.135.289.204.427.604 1.2 1.196 2.4 1.787 3.598.052.12.131.283.228.48.086.177.186.38.292.608.056.117.108.227.157.326.06.126.114.235.153.323.274-.684.552-1.366.83-2.046.278-.681.557-1.363.831-2.047.007-.038.073-.184.171-.405.148-.325.29-.653.427-.983.365-.89.804-1.96 1.78-2.253a1.737 1.737 0 0 1 1.9.663c.212.297.297.636.31.974.012.749-.35 1.553-.661 2.243-.076.168-.148.33-.212.481l-.037.083c-.086.193-.32.718-.722 1.582-.146.31-.286.62-.427.934l-.235.52Z" clip-rule="evenodd"></path></g><defs><clipPath id="a-276"><path fill="#fff" d="M0 0h71.389v36H0z"></path></clipPath></defs></svg>
                                        <div className='upgadeify-yelp-reviews-header-title-text' style={{color: style_header_text_color,}}>Reviews</div>
                                    </div>
                                    )}
                                    <div className='upgadeify-yelp-reviews-header-rating'>
                                        {Number(header_rating) === 1 && (
                                        <>
                                            <div className="upgadeify-yelp-reviews-header-rating-text" style={{color: style_header_text_color,}}>{setting_rating_format === '4.9' ? Number(apiData.data.average_rating).toFixed(1) : Number(apiData.data.average_rating).toFixed(2) }</div>
                                            <div className="upgadeify-yelp-reviews-item-rating">
                                                <RatingStars average_rating={apiData.data.average_rating} newColor={style_rating_stars_color} />
                                            </div>
                                        </>
                                        )}
                                        {Number(header_number_of_reviews) === 1 && (
                                            <span className="upgadeify-yelp-reviews-header-number-of-reviews-text">({apiData.data.total_reviews})</span>
                                        )}
                                    </div>
                                </div>
                                {Number(header_write_reviews_button) === 1 && (
                                    <div className='upgadeify-yelp-reviews-header-right'>
                                        <a
                                            href={apiData?.data?.google_url?.startsWith('http') ? apiData.data.google_url : `https://${apiData.data.google_url}`}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className='upgadeify-yelp-reviews-header-button'
                                            style={{color: style_header_write_review_text_color,backgroundColor: style_header_write_review_background_color,fontSize: header_write_review_button_type}}>Review us on Yelp
                                        </a>
                                    </div>
                                )}
                            </div>
                        )}
                    </>
                )}

                {/*If Carousel Layout Type*/}
                {selectedLayout === 'carousel_layout' && (
                    <Slider {...carouselSliderSettings}>
                        {apiData.data.review_list.map((review) => (
                            <ReviewLoopItem key={review.id} review={review} handleReadMoreClick={handleReadMoreClick} reviewTabData={reviewTabData}/>
                        ))}
                    </Slider>
                )}

                {/*If List Layout Type*/}
                {selectedLayout === 'list_layout' && (
                    <>
                        <div className='upgadeify-list_layout-layout'>
                            {apiData.data.review_list.slice(0, visibleListLayoutCount).map((review) => (
                                <ReviewLoopItem key={review.id} review={review} handleReadMoreClick={handleReadMoreClick} reviewTabData={reviewTabData}/>
                            ))}
                        </div>
                        {/* Load More Button  */}
                        {visibleListLayoutCount < apiData.data.review_list.length && (
                            <div className="load-more-button">
                                <button onClick={handleListLayoutLoadMore} style={{backgroundColor: style_load_more_button_color,color: style_load_more_button_text_color}}>Load More</button>
                            </div>
                        )}
                    </>
                )}

                {/*If Grid Layout Type*/}
                {selectedLayout === 'grid_layout' && (
                    <>
                        <div className='upgadeify-grid-layout' style={getGridLayoutStyle()}>
                            {apiData.data.review_list.slice(0, visibleGridCount).map((review) => (
                                <ReviewLoopItem key={review.id} review={review} handleReadMoreClick={handleReadMoreClick} reviewTabData={reviewTabData}/>
                            ))}
                        </div>
                        {visibleGridCount < apiData.data.review_list.length && (
                            <div className="load-more-button">
                                <button onClick={handleGridLoadMore} style={{backgroundColor: style_load_more_button_color,color: style_load_more_button_text_color}}>Load More</button>
                            </div>
                        )}
                    </>
                )}

                {/*If Masonry Layout Type*/}
                {selectedLayout === 'masonry_layout' && (
                    <>
                        <div className='upgadeify-masonry_layout-layout'>
                            {apiData.data.review_list.slice(0, visibleMasonryLayoutCount).map((review) => (
                                <ReviewLoopItem key={review.id} review={review} handleReadMoreClick={handleReadMoreClick} reviewTabData={reviewTabData}/>
                            ))}
                            
                        </div>
                            {/* Load More Button  */}
                            {visibleMasonryLayoutCount < apiData.data.review_list.length && (
                                <div className="load-more-button">
                                    <button onClick={handleMasonryLayoutLoadMore} style={{backgroundColor: style_load_more_button_color,color: style_load_more_button_text_color}}>Load More</button>
                                </div>
                            )}
                    </>
                )}

                {/*If Slider Layout Type*/}
                {selectedLayout === 'slider_layout' && (
                    <Slider {...sliderSettings}>
                        {apiData.data.review_list.map((review) => (
                            <ReviewLoopItem key={review.id} review={review} handleReadMoreClick={handleReadMoreClick} reviewTabData={reviewTabData} />
                        ))}
                    </Slider>
                )}

                {/*If Badge Layout Type*/}
                {selectedLayout === 'badge_layout' && (
                    <BadgeWidget selectedLayout={selectedLayout} isTemplateId={isTemplateId} widgetId={widgetId} reviewTabData={reviewTabData} />
                )}

                {/*If Floating Badge Layout Type*/}
                {selectedLayout === 'floating_badge_layout' && (
                    <FloatingWidget selectedLayout={selectedLayout} isTemplateId={isTemplateId} widgetId={widgetId} reviewTabData={reviewTabData} />
                )}

                {/*Common Popup */}
                {selectedReview && <ReviewPopup review={selectedReview} onClose={handleClosePopup} />}
            </>
            ) : (
                <div className="no-inner-result-found">
                    <p>No Yelp reviews found please try.</p>
                </div>
            )}
        </div >
    );
};

const ReviewLoopItem = ({ review, handleReadMoreClick, reviewTabData }) => (
    <>
        <div className="upgadeify-yelp-reviews">
            <div className='upgadeify-yelp-reviews-item' style={{ padding: `${reviewTabData.layout_item_spacing}px`, backgroundColor: reviewTabData.style_review_background_color,}}>
                <div className='upgadeify-yelp-reviews-head'>
                    {(Number(reviewTabData.review_is_show_reviewer_picture) === 1 || Number(reviewTabData.review_is_show_verified_badge) === 1) && (
                        <div className="upgadeify-yelp-reviews-item-image">
                            {review.image ? (
                                <img src={review.image} alt="Review" />
                            ) : (
                                <div className="fallback-avatar">
                                    {review.name.charAt(0)}
                                </div>
                            )}
                           
                        </div>
                    )}
                    <div className='upgadeify-yelp-reviews-item-name'>
                        {Number(reviewTabData.review_is_show_reviewer_name) === 1 && (
                        Number(reviewTabData.setting_is_enable_external_links) === 1 && review.author_url ? ( 
                                <a
                                    href={review.author_url} 
                                    target={Number(reviewTabData.setting_is_open_links_in_new_tab) === 1 ? "_blank" : "_self"}
                                    rel={Number(reviewTabData.setting_is_open_links_in_new_tab) === 1 ? "noopener noreferrer" : undefined}
                                    style={{ color: reviewTabData.style_review_reviewer_name_color }}
                                >
                                    <span className="upgadeify-yelp-reviews-item-name-text" style={{ color: reviewTabData.style_review_reviewer_name_color }}>{review.name}</span>
                                </a>
                            ) : (
                                <span className="upgadeify-yelp-reviews-item-name-text" style={{ color: reviewTabData.style_review_reviewer_name_color }}>
                                    {review.name}
                                </span>
                            )
                        )}

                        {Number(reviewTabData.review_is_show_verified_badge) === 1 && (
                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 14 14" className="review-verified-badge-icon" aria-hidden="true">
                                <path fill={reviewTabData.style_review_verified_badge_color} d="M6.757.236a.35.35 0 0 1 .486 0l1.106 1.07a.35.35 0 0 0 .329.089l1.493-.375a.35.35 0 0 1 .422.244l.422 1.48a.35.35 0 0 0 .24.24l1.481.423a.35.35 0 0 1 .244.422l-.375 1.493a.35.35 0 0 0 .088.329l1.071 1.106a.35.35 0 0 1 0 .486l-1.07 1.106a.35.35 0 0 0-.089.329l.375 1.493a.35.35 0 0 1-.244.422l-1.48.422a.35.35 0 0 0-.24.24l-.423 1.481a.35.35 0 0 1-.422.244l-1.493-.375a.35.35 0 0 0-.329.088l-1.106 1.071a.35.35 0 0 1-.486 0l-1.106-1.07a.35.35 0 0 0-.329-.089l-1.493.375a.35.35 0 0 1-.422-.244l-.422-1.48a.35.35 0 0 0-.24-.24l-1.481-.423a.35.35 0 0 1-.244-.422l.375-1.493a.35.35 0 0 0-.088-.329L.236 7.243a.35.35 0 0 1 0-.486l1.07-1.106a.35.35 0 0 0 .089-.329L1.02 3.829a.35.35 0 0 1 .244-.422l1.48-.422a.35.35 0 0 0 .24-.24l.423-1.481a.35.35 0 0 1 .422-.244l1.493.375a.35.35 0 0 0 .329-.088L6.757.236Z"></path><path fill="#fff" fill-rule="evenodd" d="M9.065 4.85a.644.644 0 0 1 .899 0 .615.615 0 0 1 .053.823l-.053.059L6.48 9.15a.645.645 0 0 1-.84.052l-.06-.052-1.66-1.527a.616.616 0 0 1 0-.882.645.645 0 0 1 .84-.052l.06.052 1.21 1.086 3.034-2.978Z" clip-rule="evenodd"></path>
                            </svg>
                        )}

                        <span className="upgadeify-yelp-reviews-item-date">
                            {Number(reviewTabData.review_is_show_date) === 1 && (
                                <span style={{color: reviewTabData.style_review_date_color,}}>- {review.review_date ? format(parseISO(review.review_date), 'yyyy-MM-dd') : ''}</span>
                            )}
                            {Number(reviewTabData.review_is_show_review_source) === 1 && (  
                                <> <a href={`https://www.yelp.com/biz/${review.review_id}`} target="blank">Yelp</a></>
                            )}
                        </span>
                    </div>
                </div>
                <div className="upgadeify-yelp-reviews-item-content">
                   {Number(reviewTabData.review_is_show_rating) === 1 && (
                        <span className="upgadeify-yelp-reviews-item-rating" style={{ fontSize: reviewTabData.style_review_rating_size }}>
                            <RatingStars average_rating={review.rating} newColor={reviewTabData.style_rating_stars_color}/>
                        </span>
                    )}
                    <div className="upgadeify-yelp-reviews-item-text" style={{color: `${reviewTabData.style_review_text_color}`,fontSize: reviewTabData.style_review_text_font_size}}>
                        {review.description.length > 50
                        ? `${review.description.slice(0, 50)}...`
                        : review.description}
                    </div>
                    {review.description.length > 50 && (
                        <div className='upgadeify-yelp-reviews-readmore' onClick={() => handleReadMoreClick(review)} style={{color: reviewTabData.style_read_more_color,fontSize: reviewTabData.style_review_text_font_size}}>
                                Read more
                        </div>
                   )}
                </div>
            </div>
        </div>
    </>
);

export default CommonWidget;
